import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

// Native-only haptic feedback — a web no-op, like iap.ts and push-notifications.ts.
//
// Every call is best-effort and fire-and-forget: a missing buzz is never worth a
// thrown error or an awaited delay in a tap handler, so nothing here is awaited by
// callers and every path swallows its own failure.
//
// The palette (see the backlog "Haptics polish" item):
//   tapLight   — reveals & small confirmations (xOVR appears, a toggle flips)
//   tapMedium  — deliberate confirms landing (lock lineup, start game, accept a suggestion)
//   celebrate  — the reward moments (Elite purchase, finish onboarding)
//   warn       — a heads-up that isn't an error (reserved; not wired yet)
//
// A single global switch lets Settings silence all of it per team preference.

let enabled = true;

/** Settings-driven master switch. Web ignores it (everything is a no-op there). */
export function setHapticsEnabled(on: boolean): void {
  enabled = on;
}

const active = () => enabled && Capacitor.isNativePlatform();

/** Soft tick — a value revealing, a toggle flipping, an attribute nudging. */
export function tapLight(): void {
  if (!active()) return;
  Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
}

/** Firm thump — a deliberate action landing (lock lineup, start game, accept). */
export function tapMedium(): void {
  if (!active()) return;
  Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {});
}

/** Rising success pattern — the celebration moments (purchase, onboarding done). */
export function celebrate(): void {
  if (!active()) return;
  Haptics.notification({ type: NotificationType.Success }).catch(() => {});
}

/** Double-buzz heads-up that isn't an error (e.g. pitcher nearing his limit). */
export function warn(): void {
  if (!active()) return;
  Haptics.notification({ type: NotificationType.Warning }).catch(() => {});
}
